import {
  ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO,
  ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO_EN,
  type CategoriaDeCumplimiento,
  type FilaDeVinculo,
  type TipoDeDocumentoDeCumplimiento,
} from '@cnc/contracts';
import {Fragment, useState} from 'react';
import {
  useCrearVinculo,
  useEditarVinculo,
  useEstadoDeCumplimiento,
  useVinculosDeCumplimiento,
} from '../api/cumplimiento';
import {et, tr} from '../i18n/idioma';
import {fecha, numero} from '../utilidades/formato';
import {CajaAjustable} from './caja-ajustable';
import {PanelDeDocumento} from './cumplimiento-documento';
import {
  COLOR_DE_SEMAFORO,
  ESTILO_DE_SITUACION,
  etiquetaDeSituacion,
  nombreDelTipo,
  textoDeDias,
} from './cumplimiento-estilos';
import {AYUDA_MATRIZ as AM} from './ayuda-cumplimiento';
import {PanelDeVinculo} from './cumplimiento-vinculo';
import {NotaDeAyuda} from './nota-de-ayuda';
import {usePermisos} from './sesion-contexto';

// Cumplimiento → una categoria: una fila por vinculo y una columna por tipo de
// documento, agrupadas por carpeta principal como en Dropbox (Compliance
// Colombia, Compliance Internacional...). Al hacer clic en una celda se abre
// el panel del documento; con ⚙ se edita el vinculo.
export function MatrizDeCumplimiento({
  categoria,
  onVerArchivo,
}: {
  categoria: CategoriaDeCumplimiento;
  onVerArchivo: (vinculoId: number) => void;
}) {
  const estado = useEstadoDeCumplimiento(categoria);
  const [buscar, setBuscar] = useState('');
  const [soloPendientes, setSoloPendientes] = useState(false);
  const [conDesactivados, setConDesactivados] = useState(false);
  const [abierta, setAbierta] = useState<{vinculoId: number; tipoId: number} | null>(null);
  const [editando, setEditando] = useState<number | null>(null);
  const e = estado.data;
  const filtro = buscar.trim().toLowerCase();
  const todas = e?.filas ?? [];
  const desactivados = todas.filter(x => !x.vinculo.activo).length;
  const filas = todas.filter(
    x =>
      (conDesactivados || x.vinculo.activo) &&
      (!filtro || x.vinculo.nombre.toLowerCase().includes(filtro)) &&
      (!soloPendientes || x.semaforo === 'rojo' || x.semaforo === 'amarillo' || x.semaforo === 'gris'),
  );
  // Grupos por carpeta principal, en el orden que trae la API.
  const grupos: Array<{seccion: string; filas: FilaDeVinculo[]}> = [];
  for (const fila of filas) {
    const seccion = fila.vinculo.seccion ?? tr('Sin carpeta', 'No folder');
    const ultimo = grupos.at(-1);
    if (ultimo?.seccion === seccion) ultimo.filas.push(fila);
    else grupos.push({seccion, filas: [fila]});
  }
  const columnas = (e?.tipos.length ?? 0) + 1;
  return (
    <div className="space-y-3" data-prueba={`cumplimiento-${categoria}`}>
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={buscar}
          onChange={x => setBuscar(x.target.value)}
          placeholder={tr('Buscar…', 'Search…')}
          aria-label={tr('Buscar vínculo', 'Search link')}
          className="w-64 rounded-md border border-linea bg-white px-3 py-1.5 text-sm"
        />
        <NotaDeAyuda titulo={tr('Buscar vínculo', 'Search link')} texto={AM.buscar} />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={soloPendientes} onChange={x => setSoloPendientes(x.target.checked)} />
          {tr('Solo con algo pendiente', 'Only with something pending')}
          <NotaDeAyuda texto={AM.soloPendientes} dentroDeEtiqueta />
        </label>
        {desactivados > 0 && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={conDesactivados} onChange={x => setConDesactivados(x.target.checked)} />
            {tr(`Mostrar los desactivados (${numero(desactivados)})`, `Show deactivated (${numero(desactivados)})`)}
            <NotaDeAyuda texto={AM.conDesactivados} dentroDeEtiqueta />
          </label>
        )}
        {e && (
          <span className="text-sm text-apagado">
            {tr(
              `${numero(filas.length)} de ${numero(todas.length)}`,
              `${numero(filas.length)} of ${numero(todas.length)}`,
            )}
          </span>
        )}
      </div>
      <p className="text-xs text-apagado">
        {categoria === 'mine'
          ? tr(
              'Las minas que se siguen. Con "✚ Agregar" se suma una; con "⚙ Editar → Quitar" se saca una que no va (si no tiene archivos). Si un documento no le aplica a una mina, ábrelo y marca "No aplica".',
              'The mines being tracked. "✚ Add" adds one; "⚙ Edit → Remove" takes out one that does not belong (if it has no files). If a document does not apply to a mine, open it and mark "Does not apply".',
            )
          : tr(
              'Un vínculo desactivado no cuenta en el resumen ni avisa; al activarlo vuelve a mostrar lo pendiente. Para un documento que no le aplica a un vínculo, ábrelo y marca "No aplica".',
              'A deactivated link does not count in the summary or warn; when activated it shows what is pending again. For a document that does not apply to a link, open it and mark "Does not apply".',
            )}
      </p>
      <AgregarVinculo categoria={categoria} />
      {estado.isError && <p className="text-sm text-alerta">{estado.error.message}</p>}
      {e && !e.tipos.length ? (
        <p className="rounded-md border border-linea bg-white p-4 text-sm text-apagado">
          {tr(
            'Esta categoría todavía no tiene tipos de documento activos. Créalos en la pestaña Tipos de documento.',
            'This category has no active document types yet. Create them in the Document types tab.',
          )}
        </p>
      ) : (
        <CajaAjustable
          altoMaximo="calc(100vh - 18rem)"
          className="overflow-auto rounded-lg border border-linea bg-white"
        >
          {estado.isPending ? (
            <p className="p-3 text-sm text-apagado">{tr('Cargando…', 'Loading…')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-white">
                <tr className="text-left align-bottom text-xs text-apagado">
                  <th className="sticky left-0 min-w-64 bg-white px-3 py-2 font-semibold tracking-wide uppercase">
                    {tr('Vínculo', 'Link')}
                    <NotaDeAyuda titulo={tr('Vínculo', 'Link')} texto={AM.vinculo} />
                  </th>
                  {e!.tipos.map(t => (
                    <th key={t.id} className="min-w-32 px-2 py-2 font-semibold" title={t.descripcion ?? undefined}>
                      {t.nombre}
                      <NotaDeAyuda titulo={t.nombre} texto={AM.tipo} />
                      {t.nombreInternacional && (
                        <span className="block font-normal">
                          {tr('Exterior:', 'Abroad:')} {t.nombreInternacional}
                        </span>
                      )}
                      {!t.obligatorio && <span className="font-normal"> ({tr('opcional', 'optional')})</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grupos.map(g => (
                  <Fragment key={g.seccion}>
                    {grupos.length > 1 && (
                      <tr className="bg-papel" data-prueba="seccion-cumplimiento">
                        <th
                          colSpan={columnas}
                          className="sticky left-0 px-3 py-1.5 text-left text-xs font-semibold tracking-wide text-verde-oscuro uppercase"
                        >
                          📁 {g.seccion} <span className="font-normal text-apagado">({numero(g.filas.length)})</span>
                        </th>
                      </tr>
                    )}
                    {g.filas.map(x => (
                      <FilaDeLaMatriz
                        key={x.vinculo.id}
                        fila={x}
                        tipos={e!.tipos}
                        onAbrir={tipoId => setAbierta({vinculoId: x.vinculo.id, tipoId})}
                        onVerArchivo={() => onVerArchivo(x.vinculo.id)}
                        onEditar={() => setEditando(x.vinculo.id)}
                      />
                    ))}
                  </Fragment>
                ))}
                {!filas.length && (
                  <tr>
                    <td colSpan={columnas} className="p-3 text-sm text-apagado">
                      {todas.length
                        ? tr('Ninguno con ese filtro.', 'None match that filter.')
                        : tr(
                            'Todavía no hay vínculos en esta categoría. Se crean al cargar la carpeta de compliance.',
                            'There are no links in this category yet. They are created when loading the compliance folder.',
                          )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </CajaAjustable>
      )}
      {abierta && <PanelDeDocumento {...abierta} onCerrar={() => setAbierta(null)} />}
      {editando !== null && (
        <PanelDeVinculo
          vinculo={todas.find(x => x.vinculo.id === editando)!.vinculo}
          onCerrar={() => setEditando(null)}
        />
      )}
    </div>
  );
}

function FilaDeLaMatriz({
  fila,
  tipos,
  onAbrir,
  onVerArchivo,
  onEditar,
}: {
  fila: FilaDeVinculo;
  tipos: TipoDeDocumentoDeCumplimiento[];
  onAbrir: (tipoId: number) => void;
  onVerArchivo: () => void;
  onEditar: () => void;
}) {
  const permisos = usePermisos();
  const editar = useEditarVinculo();
  const {vinculo} = fila;
  const cambiarActivo = () =>
    editar.mutate({id: vinculo.id, edicion: {version: vinculo.version, activo: !vinculo.activo}});
  return (
    <tr className={`border-t border-linea/70 ${vinculo.activo ? '' : 'opacity-60'}`} data-prueba="fila-cumplimiento">
      <th scope="row" className="sticky left-0 bg-white px-3 py-1.5 text-left align-top font-medium">
        <span className="flex items-center gap-2">
          <span aria-hidden className={`h-2.5 w-2.5 shrink-0 rounded-full ${COLOR_DE_SEMAFORO[fila.semaforo]}`} />
          {vinculo.nombre}
          {vinculo.internacional && (
            <span className="text-xs font-normal text-apagado" title={tr('Empresa del exterior', 'Foreign company')}>
              🌐
            </span>
          )}
        </span>
        <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 pl-4.5 text-xs font-normal text-apagado">
          {permisos.editar ? (
            <button
              type="button"
              onClick={cambiarActivo}
              disabled={editar.isPending}
              aria-pressed={vinculo.activo}
              title={
                vinculo.activo
                  ? tr('Desactivar: deja de contar y de avisar', 'Deactivate: stops counting and warning')
                  : tr('Activar: vuelve a mostrar lo pendiente', 'Activate: shows what is pending again')
              }
              className={`rounded-full border px-2 py-0.5 ${
                vinculo.activo
                  ? 'border-verde/40 bg-verde-claro text-verde-oscuro'
                  : 'border-linea bg-white text-apagado'
              }`}
              data-prueba="activar-vinculo"
            >
              {vinculo.activo ? `● ${tr('Activo', 'Active')}` : `○ ${tr('Desactivado', 'Deactivated')}`}
            </button>
          ) : (
            <span>{vinculo.activo ? tr('Activo', 'Active') : tr('Desactivado', 'Deactivated')}</span>
          )}
          <NotaDeAyuda texto={AM.activo} />
          <button type="button" onClick={onVerArchivo} className="text-verde hover:underline">
            {tr(`${numero(vinculo.documentos)} archivos`, `${numero(vinculo.documentos)} files`)}
          </button>
          {fila.sinClasificar > 0 && (
            <span>
              {tr(`${numero(fila.sinClasificar)} sin clasificar`, `${numero(fila.sinClasificar)} unclassified`)}
            </span>
          )}
          {vinculo.ultimaRecepcion && (
            <span>
              {tr(`último: ${fecha(vinculo.ultimaRecepcion)}`, `latest: ${fecha(vinculo.ultimaRecepcion)}`)}
              <NotaDeAyuda texto={AM.ultimo} />
            </span>
          )}
          {permisos.editar && (
            <button
              type="button"
              onClick={onEditar}
              className="text-verde hover:underline"
              aria-label={tr(`Editar ${vinculo.nombre}`, `Edit ${vinculo.nombre}`)}
            >
              ⚙ {tr('Editar', 'Edit')}
            </button>
          )}
        </span>
        {editar.isError && <span className="block text-xs text-alerta">{editar.error.message}</span>}
      </th>
      {fila.celdas.map(c => {
        const estilo = ESTILO_DE_SITUACION[c.situacion];
        const tipo = tipos.find(t => t.id === c.tipoId)!;
        const nombre = nombreDelTipo(tipo, vinculo);
        return (
          <td key={c.tipoId} className="px-2 py-1.5 align-top">
            <button
              type="button"
              onClick={() => onAbrir(c.tipoId)}
              aria-label={`${nombre} · ${vinculo.nombre}: ${etiquetaDeSituacion(c.situacion)}`}
              title={[nombre, etiquetaDeSituacion(c.situacion), textoDeDias(c.diasParaVencer)]
                .filter(Boolean)
                .join(' · ')}
              className={`flex w-full flex-col items-start rounded-md border px-2 py-1 text-left text-xs hover:shadow-sm ${estilo.clase}`}
            >
              <span className="font-medium">
                <span aria-hidden>{estilo.icono}</span> {etiquetaDeSituacion(c.situacion)}
              </span>
              {c.venceEl && c.aplica && <span className="opacity-80">{fecha(c.venceEl)}</span>}
            </button>
          </td>
        );
      })}
    </tr>
  );
}

// Agregar un vinculo a esta pestana: uno nuevo (p. ej. una mina) o uno que ya
// existe en otra categoria (sus documentos se reconocen con los tipos de esta).
function AgregarVinculo({categoria}: {categoria: CategoriaDeCumplimiento}) {
  const permisos = usePermisos();
  const todos = useVinculosDeCumplimiento();
  const crear = useCrearVinculo();
  const mover = useEditarVinculo();
  const [abierto, setAbierto] = useState(false);
  const [nombre, setNombre] = useState('');
  const [existente, setExistente] = useState('');
  const categorias = et(ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO, ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO_EN);
  if (!permisos.editar) return null;
  if (!abierto)
    return (
      <button
        type="button"
        onClick={() => setAbierto(true)}
        className="rounded-md border border-verde bg-white px-3 py-1.5 text-sm text-verde-oscuro hover:bg-verde-claro"
      >
        ✚ {tr(`Agregar a ${categorias[categoria]}`, `Add to ${categorias[categoria]}`)}
      </button>
    );
  const otros = (todos.data?.vinculos ?? [])
    .filter(x => x.categoria !== categoria)
    .sort((a, b) => a.nombre.localeCompare(b.nombre));
  const elegido = otros.find(x => String(x.id) === existente);
  const listo = () => {
    setNombre('');
    setExistente('');
    setAbierto(false);
  };
  const campo = 'rounded-md border border-linea bg-white px-2 py-1.5 text-sm';
  return (
    <div className="space-y-2 rounded-md border border-linea bg-papel p-3" data-prueba="agregar-vinculo">
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={e => {
          e.preventDefault();
          crear.mutate({categoria, nombre: nombre.trim()}, {onSuccess: listo});
        }}
      >
        <span className="text-sm">{tr('Nuevo:', 'New:')}</span>
        <input
          value={nombre}
          onChange={e => setNombre(e.target.value)}
          placeholder={tr('Nombre de la empresa', 'Company name')}
          aria-label={tr('Nombre del vínculo nuevo', 'New link name')}
          className={`${campo} w-64`}
        />
        <button
          type="submit"
          disabled={!nombre.trim() || crear.isPending}
          className="rounded-md bg-verde px-3 py-1.5 text-sm text-white hover:bg-verde-oscuro disabled:opacity-50"
        >
          {tr('Agregar', 'Add')}
        </button>
        <button type="button" onClick={listo} className="text-sm text-apagado hover:underline">
          {tr('Cancelar', 'Cancel')}
        </button>
      </form>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm">{tr('O traer uno que ya existe:', 'Or bring an existing one:')}</span>
        <select
          aria-label={tr('Vínculo que ya existe', 'Existing link')}
          value={existente}
          onChange={e => setExistente(e.target.value)}
          className={`${campo} max-w-md`}
        >
          <option value="">{tr('Elige…', 'Choose…')}</option>
          {otros.map(x => (
            <option key={x.id} value={x.id}>
              {x.nombre} ({categorias[x.categoria]}, {numero(x.documentos)} {tr('archivos', 'files')})
            </option>
          ))}
        </select>
        {elegido && (
          <button
            type="button"
            disabled={mover.isPending}
            onClick={() =>
              mover.mutate({id: elegido.id, edicion: {version: elegido.version, categoria}}, {onSuccess: listo})
            }
            className="rounded-md border border-verde bg-white px-3 py-1.5 text-sm text-verde-oscuro hover:bg-verde-claro disabled:opacity-50"
          >
            {tr(`Traer a ${categorias[categoria]}`, `Bring to ${categorias[categoria]}`)}
          </button>
        )}
      </div>
      {(crear.isError || mover.isError) && (
        <p className="text-xs text-alerta">{(crear.error ?? mover.error)?.message}</p>
      )}
    </div>
  );
}
